import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { browserTimeZone, canonicalZone, formatDateTimeZone, formatMyTime, formatPlaceTime, instantToZoned, listTimeZones, searchTimeZones, timeZoneOption, timeZonePrimaryOptionLabel, zoneCity, zoneLabel, zoneName, zoneOffsetMinutes, zonedTimeNote, zonedToInstant, } from "@sequentech/ui-core";
/**
 * The viewer's zone, "my time": the browser's, unless a story or test fixes
 * it, so what they show never depends on the machine they run on.
 */
var MyTimeZoneContext = createContext(null);
export var MyTimeZoneProvider = function (_a) {
    var zone = _a.zone, children = _a.children;
    return _jsx(MyTimeZoneContext.Provider, { value: zone, children: children });
};
/**
 * `{{dateTime}}` in admin screens: the i18n language's medium date and a
 * 24-hour time, as the wall time in `zone`. The zone itself is never part of
 * the pattern; the `timezones.*` strings place it.
 */
export var adminDateTimeFormat = function (language) {
    var locale = language.split("-")[0] === "cat" ? "ca" : language;
    return function (instant, zone) {
        var options = {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
            timeZone: zone,
        };
        try {
            return new Intl.DateTimeFormat(locale, options).format(instant);
        }
        catch (_a) {
            // A language Intl doesn't know (e.g. a custom code): the default locale.
            return new Intl.DateTimeFormat(undefined, options).format(instant);
        }
    };
};
/**
 * A wall time (`YYYY-MM-DDTHH:MM`) in the admin format as written, without
 * resolving it in a zone, so a time that doesn't exist (a DST gap) can be named.
 */
export var formatWallTime = function (local, options) {
    var _a;
    var match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
    if (!match)
        return local;
    var _b = match.map(Number), year = _b[1], month = _b[2], day = _b[3], hour = _b[4], minute = _b[5];
    var asUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
    asUtc.setUTCFullYear(year);
    return ((_a = options.formatDateTime) !== null && _a !== void 0 ? _a : adminDateTimeFormat(options.lang))(asUtc, "UTC");
};
/** The timezone service with the admin's texts bound. */
export var useTimeZoneService = function () {
    var _a;
    var fixed = useContext(MyTimeZoneContext);
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var language = (_a = i18n === null || i18n === void 0 ? void 0 : i18n.language) !== null && _a !== void 0 ? _a : "en";
    return useMemo(function () {
        var text = {
            t: t,
            lang: language,
            formatDateTime: adminDateTimeFormat(language),
        };
        var myTimeZone = fixed !== null && fixed !== void 0 ? fixed : browserTimeZone();
        return {
            listTimeZones: listTimeZones,
            canonicalZone: canonicalZone,
            searchTimeZones: searchTimeZones,
            timeZoneOption: timeZoneOption,
            timeZonePrimaryOptionLabel: timeZonePrimaryOptionLabel,
            zoneOffsetMinutes: zoneOffsetMinutes,
            zonedToInstant: zonedToInstant,
            instantToZoned: instantToZoned,
            zoneLabel: zoneLabel,
            zoneName: zoneName,
            zoneCity: zoneCity,
            formatDateTimeZone: formatDateTimeZone,
            formatPlaceTime: formatPlaceTime,
            zonedTimeNote: zonedTimeNote,
            myTimeZone: myTimeZone,
            formatMyTime: function (instant) { return formatMyTime(instant, text, myTimeZone); },
            text: text,
        };
    }, [fixed, t, language]);
};
